import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Scale, Plus, Trash2, TrendingDown, TrendingUp, Loader2, X, 
  Ruler, Percent, Calendar, ArrowRight, Target, Activity, Zap,
  ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { toast } from "sonner";
import Paywall from "@/components/Paywall";

// Feature card for empty state
function FeatureCard({ icon: Icon, title, description, color }) {
  return (
    <div className={`p-5 rounded-xl bg-gradient-to-br ${color} border border-white/5`}>
      <Icon className="h-8 w-8 mb-3 text-white/80" />
      <h3 className="font-bold text-white mb-1">{title}</h3>
      <p className="text-xs text-white/60">{description}</p>
    </div>
  );
}

export default function BodyTracker() {
  const { authHeaders } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [form, setForm] = useState({
    weight: "", body_fat_percentage: "", chest: "", waist: "", hips: "", arms: "", thighs: "", notes: ""
  });

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/body-logs`, { headers: authHeaders() });
      setLogs(res.data);
    } catch { toast.error("Kunne ikke hente logs"); } 
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.weight && !form.body_fat_percentage) {
      toast.error("Indtast mindst vægt eller fedtprocent");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/body-logs`, {
        weight: form.weight ? parseFloat(form.weight) : null,
        body_fat_percentage: form.body_fat_percentage ? parseFloat(form.body_fat_percentage) : null,
        chest: form.chest ? parseFloat(form.chest) : null,
        waist: form.waist ? parseFloat(form.waist) : null,
        hips: form.hips ? parseFloat(form.hips) : null,
        arms: form.arms ? parseFloat(form.arms) : null,
        thighs: form.thighs ? parseFloat(form.thighs) : null,
        notes: form.notes
      }, { headers: authHeaders() });
      toast.success("Log gemt!");
      setForm({ weight: "", body_fat_percentage: "", chest: "", waist: "", hips: "", arms: "", thighs: "", notes: "" });
      setShowForm(false);
      fetchLogs();
    } catch { toast.error("Fejl ved gemning"); } 
    finally { setSaving(false); }
  };

  const deleteLog = async (id) => {
    try {
      await axios.delete(`${API}/body-logs/${id}`, { headers: authHeaders() });
      toast.success("Log slettet");
      fetchLogs();
    } catch { toast.error("Fejl ved sletning"); }
  };

  const chartData = logs.slice().reverse().map(log => ({
    date: new Date(log.date).toLocaleDateString("da-DK", { day: "numeric", month: "short" }),
    weight: log.weight,
    bodyFat: log.body_fat_percentage
  }));

  const latestLog = logs[0];
  const prevLog = logs[1];
  const weightChange = latestLog?.weight && prevLog?.weight ? (latestLog.weight - prevLog.weight).toFixed(1) : null;
  const fatChange = latestLog?.body_fat_percentage && prevLog?.body_fat_percentage 
    ? (latestLog.body_fat_percentage - prevLog.body_fat_percentage).toFixed(1) : null;

  // Calculate trends
  const firstLog = logs[logs.length - 1];
  const totalWeightChange = latestLog?.weight && firstLog?.weight 
    ? (latestLog.weight - firstLog.weight).toFixed(1) : null;

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]" data-testid="body-tracker-page">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-pink-500/10 via-transparent to-transparent" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-[#DFFF00]/5 rounded-full blur-3xl" />
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500/20 to-pink-500/5 border border-pink-500/20 flex items-center justify-center">
                  <Scale className="h-7 w-7 text-pink-400" />
                </div>
                <div>
                  <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white">
                    Krops<span className="text-pink-400">tracker</span>
                  </h1>
                  <p className="text-sm text-zinc-400 mt-0.5">
                    {logs.length > 0 ? `${logs.length} målinger logget` : "Følg din krops udvikling"}
                  </p>
                </div>
              </div>
              <Button onClick={() => setShowForm(!showForm)} data-testid="add-body-log-btn"
                className="bg-gradient-to-r from-pink-500 to-pink-600 text-white hover:opacity-90 shadow-lg shadow-pink-500/20 rounded-xl font-bold uppercase tracking-wider h-12 px-6">
                <Plus className="mr-2 h-5 w-5" /> Ny Måling
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          {/* New Log Form */}
          {showForm && (
            <div className="bg-gradient-to-br from-[#18181b] to-[#141417] border border-pink-500/20 rounded-2xl mb-8 overflow-hidden shadow-xl shadow-pink-500/5" data-testid="body-log-form">
              <div className="p-6 border-b border-white/5 bg-gradient-to-r from-pink-500/10 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
                      <Plus className="h-5 w-5 text-pink-400" />
                    </div>
                    <div>
                      <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-xl tracking-wider text-white">Ny Måling</h2>
                      <p className="text-xs text-zinc-500">Log din vægt, fedtprocent og mål</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white">
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Primary measurements */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
                    <Target className="h-3 w-3" /> Primære målinger
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                        <Scale className="h-3 w-3 text-pink-400" /> Vægt (kg)
                      </Label>
                      <Input type="number" step="0.1" value={form.weight} onChange={e => setForm({...form, weight: e.target.value})}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-pink-500 rounded-xl h-12" placeholder="75.0" data-testid="weight-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                        <Percent className="h-3 w-3 text-blue-400" /> Fedtprocent (%)
                      </Label>
                      <Input type="number" step="0.1" value={form.body_fat_percentage} onChange={e => setForm({...form, body_fat_percentage: e.target.value})}
                        className="bg-zinc-900/50 border-zinc-800 focus:border-blue-500 rounded-xl h-12" placeholder="18.0" data-testid="bodyfat-input" />
                    </div>
                  </div>
                </div>

                {/* Body measurements */}
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
                    <Ruler className="h-3 w-3" /> Kropsmål (cm) - valgfrit
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                    {[
                      { key: "chest", label: "Bryst", color: "text-amber-400" },
                      { key: "waist", label: "Talje", color: "text-orange-400" },
                      { key: "hips", label: "Hofter", color: "text-purple-400" },
                      { key: "arms", label: "Arme", color: "text-cyan-400" },
                      { key: "thighs", label: "Lår", color: "text-emerald-400" },
                    ].map(({ key, label, color }) => (
                      <div key={key} className="space-y-2">
                        <Label className={`text-xs uppercase tracking-wider text-zinc-500`}>{label}</Label>
                        <Input type="number" step="0.1" value={form[key]} onChange={e => setForm({...form, [key]: e.target.value})}
                          className="bg-zinc-900/50 border-zinc-800 focus:border-white/20 rounded-xl h-11" placeholder="0.0" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">Noter (valgfrit)</Label>
                  <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                    className="bg-zinc-900/50 border-zinc-800 focus:border-pink-500 rounded-xl h-12" placeholder="Hvordan føler du dig?" />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-white/5">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-white/10 text-zinc-400 rounded-xl">
                    Annuller
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="save-body-log-btn"
                    className="flex-1 bg-gradient-to-r from-pink-500 to-pink-600 text-white hover:opacity-90 rounded-xl font-bold uppercase tracking-wider h-12">
                    {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Gemmer...</> : "Gem Måling"}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-pink-400 mx-auto" />
            </div>
          ) : logs.length === 0 ? (
            /* Empty state */
            <div className="text-center py-12">
              <div className="max-w-2xl mx-auto">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-pink-500/20 to-pink-500/5 border border-pink-500/20 flex items-center justify-center">
                  <Scale className="h-10 w-10 text-pink-400/50" />
                </div>
                <h2 style={{ fontFamily: 'Oswald' }} className="text-2xl font-bold uppercase text-white mb-2">
                  Start din krops-tracking
                </h2>
                <p className="text-zinc-400 mb-8 max-w-md mx-auto">
                  Log din vægt, fedtprocent og kropsmål regelmæssigt for at følge din fremgang!
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <FeatureCard
                    icon={TrendingDown}
                    title="Følg Fremgang"
                    description="Se din udvikling med detaljerede grafer"
                    color="from-pink-500/20 to-pink-500/5"
                  />
                  <FeatureCard
                    icon={Ruler}
                    title="Alle Mål"
                    description="Vægt, fedt, talje, arme, lår og mere"
                    color="from-blue-500/20 to-blue-500/5"
                  />
                  <FeatureCard
                    icon={Calendar}
                    title="Historik"
                    description="Se alle dine tidligere målinger"
                    color="from-purple-500/20 to-purple-500/5"
                  />
                </div>
                
                <Button onClick={() => setShowForm(true)}
                  className="bg-gradient-to-r from-pink-500 to-pink-600 text-white hover:opacity-90 shadow-lg shadow-pink-500/20 rounded-xl font-bold uppercase tracking-wider h-12 px-8">
                  <Plus className="mr-2 h-4 w-4" /> Log din første måling
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {/* Current Weight */}
                <div className="bg-gradient-to-br from-pink-500/20 to-transparent bg-[#18181b] border border-white/5 rounded-xl p-5 hover:border-pink-500/20 transition-all">
                  <Scale className="h-5 w-5 text-pink-400 mb-3" />
                  <p style={{ fontFamily: 'JetBrains Mono' }} className="text-3xl font-bold text-pink-400" data-testid="current-weight">
                    {latestLog?.weight ? latestLog.weight : "-"}
                  </p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Vægt (kg)</p>
                  {weightChange && (
                    <div className={`flex items-center gap-1 mt-2 text-xs ${parseFloat(weightChange) < 0 ? 'text-emerald-400' : 'text-orange-400'}`}>
                      {parseFloat(weightChange) < 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                      {parseFloat(weightChange) > 0 ? '+' : ''}{weightChange} kg
                    </div>
                  )}
                </div>

                {/* Body Fat */}
                <div className="bg-gradient-to-br from-blue-500/20 to-transparent bg-[#18181b] border border-white/5 rounded-xl p-5 hover:border-blue-500/20 transition-all">
                  <Percent className="h-5 w-5 text-blue-400 mb-3" />
                  <p style={{ fontFamily: 'JetBrains Mono' }} className="text-3xl font-bold text-blue-400" data-testid="current-bodyfat">
                    {latestLog?.body_fat_percentage ? latestLog.body_fat_percentage : "-"}
                  </p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Fedtprocent (%)</p>
                  {fatChange && (
                    <div className={`flex items-center gap-1 mt-2 text-xs ${parseFloat(fatChange) < 0 ? 'text-emerald-400' : 'text-orange-400'}`}>
                      {parseFloat(fatChange) < 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
                      {parseFloat(fatChange) > 0 ? '+' : ''}{fatChange}%
                    </div>
                  )}
                </div>

                {/* Waist */}
                <div className="bg-gradient-to-br from-orange-500/20 to-transparent bg-[#18181b] border border-white/5 rounded-xl p-5 hover:border-orange-500/20 transition-all">
                  <Ruler className="h-5 w-5 text-orange-400 mb-3" />
                  <p style={{ fontFamily: 'JetBrains Mono' }} className="text-3xl font-bold text-orange-400">
                    {latestLog?.measurements?.waist || "-"}
                  </p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Talje (cm)</p>
                </div>

                {/* Total logs / Progress */}
                <div className="bg-gradient-to-br from-purple-500/20 to-transparent bg-[#18181b] border border-white/5 rounded-xl p-5 hover:border-purple-500/20 transition-all">
                  <Activity className="h-5 w-5 text-purple-400 mb-3" />
                  <p style={{ fontFamily: 'JetBrains Mono' }} className="text-3xl font-bold text-purple-400">
                    {logs.length}
                  </p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Målinger</p>
                  {totalWeightChange && logs.length > 1 && (
                    <div className={`flex items-center gap-1 mt-2 text-xs ${parseFloat(totalWeightChange) < 0 ? 'text-emerald-400' : 'text-orange-400'}`}>
                      Total: {parseFloat(totalWeightChange) > 0 ? '+' : ''}{totalWeightChange} kg
                    </div>
                  )}
                </div>
              </div>

              {/* Weight Chart */}
              {chartData.length > 1 && (
                <div className="bg-[#18181b] border border-white/5 rounded-2xl overflow-hidden mb-8" data-testid="body-tracker-chart">
                  <div className="p-5 border-b border-white/5 flex items-center justify-between">
                    <div>
                      <h2 style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Vægt Udvikling</h2>
                      <p className="text-xs text-zinc-500">De seneste {chartData.length} målinger</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-pink-400" />
                        <span className="text-zinc-400">Vægt</span>
                      </div>
                      {chartData.some(d => d.bodyFat) && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-400" />
                          <span className="text-zinc-400">Fedtprocent</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-5 h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#ec4899" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="fatGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#60a5fa" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} domain={['dataMin - 2', 'dataMax + 2']} />
                        <Tooltip
                          contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                          formatter={(value, name) => [value ? `${value} ${name === 'weight' ? 'kg' : '%'}` : '-', name === 'weight' ? 'Vægt' : 'Fedtprocent']}
                        />
                        <Area type="monotone" dataKey="weight" stroke="#ec4899" strokeWidth={2} fill="url(#weightGradient)" />
                        {chartData.some(d => d.bodyFat) && (
                          <Area type="monotone" dataKey="bodyFat" stroke="#60a5fa" strokeWidth={2} fill="url(#fatGradient)" />
                        )}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Log History */}
              <div className="bg-[#18181b] border border-white/5 rounded-2xl overflow-hidden">
                <div className="p-5 border-b border-white/5 flex items-center justify-between">
                  <div>
                    <h2 style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Måling Historik</h2>
                    <p className="text-xs text-zinc-500">{logs.length} målinger total</p>
                  </div>
                </div>
                <div className="divide-y divide-white/5">
                  {logs.map((log, idx) => (
                    <div key={log.id} className="group" data-testid={`body-log-${log.id}`}>
                      <div className="p-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors cursor-pointer"
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                        {/* Date indicator */}
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500/10 to-transparent flex flex-col items-center justify-center flex-shrink-0">
                          <span style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-pink-400">
                            {new Date(log.date).getDate()}
                          </span>
                          <span className="text-[9px] uppercase text-zinc-500">
                            {new Date(log.date).toLocaleDateString("da-DK", { month: 'short' })}
                          </span>
                        </div>

                        {/* Main stats */}
                        <div className="flex-1 flex items-center gap-6 flex-wrap">
                          {log.weight && (
                            <div>
                              <p className="text-[10px] text-zinc-600 uppercase tracking-wider">Vægt</p>
                              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-pink-400">{log.weight} kg</p>
                            </div>
                          )}
                          {log.body_fat_percentage && (
                            <div>
                              <p className="text-[10px] text-zinc-600 uppercase tracking-wider">Fedt</p>
                              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-blue-400">{log.body_fat_percentage}%</p>
                            </div>
                          )}
                          {log.measurements?.waist && (
                            <div className="hidden sm:block">
                              <p className="text-[10px] text-zinc-600 uppercase tracking-wider">Talje</p>
                              <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-orange-400">{log.measurements.waist} cm</p>
                            </div>
                          )}
                          {idx === 0 && (
                            <span className="bg-pink-500/10 text-pink-400 text-[10px] font-bold px-2 py-1 rounded-lg uppercase">Seneste</span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" 
                            className="text-zinc-600 hover:text-white hover:bg-white/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            {expandedLog === log.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); deleteLog(log.id); }}
                            className="text-zinc-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" 
                            data-testid={`delete-body-log-${log.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {expandedLog === log.id && (
                        <div className="px-5 pb-5 pt-0">
                          <div className="bg-zinc-900/50 rounded-xl p-4 border border-white/5">
                            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center gap-2">
                              <Ruler className="h-3 w-3" /> Alle mål
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                              {[
                                { label: "Bryst", value: log.measurements?.chest, color: "text-amber-400" },
                                { label: "Talje", value: log.measurements?.waist, color: "text-orange-400" },
                                { label: "Hofter", value: log.measurements?.hips, color: "text-purple-400" },
                                { label: "Arme", value: log.measurements?.arms, color: "text-cyan-400" },
                                { label: "Lår", value: log.measurements?.thighs, color: "text-emerald-400" },
                              ].map(({ label, value, color }) => (
                                <div key={label}>
                                  <p className="text-[10px] text-zinc-600 uppercase">{label}</p>
                                  <p style={{ fontFamily: 'JetBrains Mono' }} className={`text-sm font-bold ${value ? color : 'text-zinc-700'}`}>
                                    {value ? `${value} cm` : '-'}
                                  </p>
                                </div>
                              ))}
                            </div>
                            {log.notes && (
                              <div className="mt-4 pt-4 border-t border-white/5 flex items-start gap-2">
                                <Zap className="h-4 w-4 text-pink-400 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-zinc-400">{log.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Paywall>
  );
}
